import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';

import { FeedKey, SortMode } from 'api';
import { EmptyState, Layouts, LeaderboardWidget } from 'components';
import { Feed, FeedSelect } from 'features/feed';
import { DEFAULT_SORT, FEED_LIMIT, FOLLOW_QUERIES_LIMIT } from 'app-constants';
import {
  useProtectedClient,
  useCurrentUser,
  useFollowed,
  useLocalStorage,
  useTimeline,
  useFollowedAllResultsQuery,
} from 'hooks';
import { SetupProfileButton, SetupWorkspaceButton } from 'features/setup';
import * as paths from 'paths';
import * as Links from 'Links';

import {
  CreateGroup,
  FeedMenu,
  InviteModule,
  RecentActivity,
  RecommendedGroups,
  RecommendedPeople,
  TotalReach,
  Welcome,
} from '../components';

const TimelineIndex: FC = () => {
  const [s, setSort] = useLocalStorage('timelineSort', DEFAULT_SORT);
  const { timelineFeed, timelineGroup } = useTimeline();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const client = useProtectedClient();
  const { postable } = useFollowed();
  const history = useHistory();
  const location = useLocation();

  // Refetch group follows to ensure list is current
  useFollowedAllResultsQuery({
    data: { user_id: currentUser.id, limit: FOLLOW_QUERIES_LIMIT },
  });

  const sort = client.sort_mode ? client.sort_mode === SortMode.Trending : s;

  const { hasTimeline } = postable;
  const selectedGroup = hasTimeline ? timelineGroup : null;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shareUrl = searchParams.get('share-url');

    if (shareUrl) {
      history.replace(`${paths.postCreate}?share-url=${encodeURIComponent(shareUrl)}`);
    }
  }, []);

  function handleSort() {
    setSort(!sort);
  }

  function renderEmptyState() {
    return (
      <EmptyState
        box
        name="timeline"
        actionComponent={
          <Box paddingTop={2}>
            <Button component={Links.PostCreate} color="primary" variant="contained" size="large">
              {t('emptyStates:timelineActionText')}
            </Button>
          </Box>
        }
      />
    );
  }

  function renderMain() {
    const variables = {
      sort,
      key: FeedKey.Timeline,
      id: timelineFeed.id,
      limit: FEED_LIMIT,
    };

    return (
      <Layouts.AppMain2Column
        hideRightOnMobile
        desktopSplit="8/4"
        left={
          <Feed
            emptyState={renderEmptyState}
            variables={variables}
            selectedGroup={selectedGroup}
            header={<Welcome />}
          />
        }
        right={renderRightSidebar()}
      />
    );
  }

  function renderRightSidebar() {
    return (
      <Grid container spacing={3} direction="column">
        {client.allow_user_invites && (
          <Grid item xs>
            <InviteModule />
          </Grid>
        )}
        <Grid item xs>
          <TotalReach />
        </Grid>
        <CreateGroup />
        {currentUser.leaderboard_enabled && (
          <LeaderboardWidget variables={{ user_id: currentUser.id }} />
        )}
        <RecentActivity />
        <RecommendedPeople />
        <RecommendedGroups />
      </Grid>
    );
  }

  return (
    <Layouts.App2Column
      sidebar={
        <FeedSelect menu={client.sort_mode ? null : <FeedMenu sort={sort} onSort={handleSort} />}>
          <SetupWorkspaceButton />
          <SetupProfileButton />
        </FeedSelect>
      }
      main={renderMain()}
    />
  );
};

export default TimelineIndex;
